import { springLinear } from "@/lib/desk-motion";

/** Same spring as `--plus-spring` in src/styles/plus-menu.css. */
const SPRING = springLinear(14, 0.7, 8.5);

/** How narrow the capsule gets, as a fraction of its width, on open/close. */
export const SQUASH_OPEN = 0.74;
export const SQUASH_CLOSE = 0.86;
export const SQUASH_OPEN_MS = 520;
export const SQUASH_CLOSE_MS = 380;

/**
 * Keyframes of the "gooey" squeeze the tab bar does as it turns into the +
 * panel (and back): it pinches in towards its centre, then springs back out
 * to its full width while the panel grows upward (a CSS height transition).
 * Opening pinches early (the blob forms before it grows); closing pinches
 * late, as the panel sinks back into the capsule.
 */
export const squashKeyframes = (open: boolean): Keyframe[] =>
  open
    ? [
        { transform: "scaleX(1)", easing: "cubic-bezier(0.3, 0, 0.6, 1)" },
        { transform: `scaleX(${SQUASH_OPEN})`, offset: 0.26, easing: SPRING },
        { transform: "scaleX(1)" },
      ]
    : [
        { transform: "scaleX(1)", easing: "cubic-bezier(0.4, 0, 0.7, 1)" },
        { transform: `scaleX(${SQUASH_CLOSE})`, offset: 0.5, easing: SPRING },
        { transform: "scaleX(1)" },
      ];

/**
 * Plays the squeeze on `element`. A squeeze still running (the + was tapped
 * again mid-way) is played back from where it is instead, so the motion
 * reverses smoothly rather than jumping. Nothing is left behind once it
 * finishes: no fill, so the element ends without a transform.
 */
export const playSquash = (
  element: HTMLElement,
  open: boolean,
  running: Animation | null,
): Animation | null => {
  if (typeof element.animate !== "function") return null;
  if (running && running.playState === "running") {
    running.reverse();
    return running;
  }
  running?.cancel();
  return element.animate(squashKeyframes(open), {
    duration: open ? SQUASH_OPEN_MS : SQUASH_CLOSE_MS,
    fill: "none",
  });
};

/** A quick swell of the + as it turns into an x (and back). */
export const playPop = (element: HTMLElement): Animation | null => {
  if (typeof element.animate !== "function") return null;
  return element.animate(
    [
      { scale: "1" },
      { scale: "1.12", offset: 0.35, easing: SPRING },
      { scale: "1" },
    ],
    { duration: 360, easing: "ease-out", fill: "none" },
  );
};
